/**
 * Public transfer acceptance page (NOS-169): /transfer-accept?token=...
 * The designated new owner signs in (or up), accepts, and becomes the child
 * org's owner — cutover happens server-side in the sponsor-transfer function.
 */
import { useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { supabase } from "@/lib/supabase"
import { useAuthStore } from "@/stores/authStore"
import { useOrgStore } from "@/stores/orgStore"

export function TransferAcceptPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { user, isLoading: authLoading } = useAuthStore()
  const { fetchUserOrgs, switchOrg } = useOrgStore()
  const token = searchParams.get("token")

  const [accepting, setAccepting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleAccept = async () => {
    if (!token) return
    setAccepting(true)
    setError(null)
    const { data: sess } = await supabase.auth.getSession()
    const { data, error: fnError } = await supabase.functions.invoke("sponsor-transfer", {
      body: { action: "accept", token },
      ...(sess.session?.access_token
        ? { headers: { Authorization: `Bearer ${sess.session.access_token}` } }
        : {}),
    })
    setAccepting(false)
    if (fnError || !data?.ok) {
      // Surface the function's specific message (expired / already completed / …)
      let message = "Failed to accept the transfer"
      const ctx = (fnError as { context?: Response } | null)?.context
      if (ctx instanceof Response) {
        try {
          const body = (await ctx.clone().json()) as { error?: string }
          if (body?.error) message = body.error
        } catch {
          /* keep default */
        }
      }
      setError(message)
      return
    }
    const org = data.org as { id: string; name: string } | null
    toast.success(`You now own ${org?.name ?? "the organization"}!`)
    await fetchUserOrgs()
    if (org?.id) await switchOrg(org.id)
    // Guided setup (Stripe + legal identity) — deliberately NOT the site
    // onboarding: the org already has a live site.
    navigate("/transfer-welcome")
  }

  const redirect = `/transfer-accept?token=${token ?? ""}`

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Organization transfer</CardTitle>
          <CardDescription>
            A fiscal sponsor is transferring an organization to you. Accepting makes you its owner
            — website, donors, and history included.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!token && (
            <p className="text-center text-sm text-destructive">Invalid transfer link.</p>
          )}
          {error && <p className="text-center text-sm text-destructive">{error}</p>}
          {token && !authLoading && !user && (
            <div className="flex flex-col gap-3">
              <p className="text-center text-sm text-muted-foreground">
                Sign up or log in to accept this transfer.
              </p>
              <Button onClick={() => navigate(`/signup?redirect=${encodeURIComponent(redirect)}`)}>
                Create Account
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate(`/login?redirect=${encodeURIComponent(redirect)}`)}
              >
                Log In
              </Button>
            </div>
          )}
          {token && user && (
            <div className="flex flex-col gap-3">
              <p className="text-center text-sm text-muted-foreground">
                Logged in as <strong>{user.email}</strong>
              </p>
              <Button onClick={handleAccept} disabled={accepting}>
                {accepting ? "Accepting..." : "Accept transfer"}
              </Button>
              <p className="text-center text-xs text-muted-foreground">
                After accepting, you'll connect your own Stripe account and enter your EIN before
                donations resume.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
